/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useState } from 'react'

import { TbTruckDelivery } from 'react-icons/tb'
import { Link, useLocation } from 'react-router-dom'

import { Alert, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'
import logger from '@/utils/logger'

import { ShipmentForm } from './ShipmentForm'
import type { CreateShipmentBody } from './types'

const log = logger({ category: 'NewShipmentPage' })

const NewShipmentPage: React.FC = () => {
  const { api } = useApi()
  // Reached from both the tracker map and the admin index — go back to whichever one.
  const cameFromTrackers = (useLocation().state as { from?: string } | null)?.from === 'trackers'
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [created, setCreated] = useState(false)
  // Bumping this remounts the form, which is how it gets cleared — see ShipmentForm.
  const [formKey, setFormKey] = useState(0)

  const clearForm = () => {
    setFormKey((key) => key + 1)
    setSaveError('')
  }

  const handleSubmit = async (body: CreateShipmentBody) => {
    setSaveError('')
    setCreated(false)
    setIsSaving(true)
    try {
      const response = await api.post<{ id: number }, CreateShipmentBody>({
        path: '/shipments',
        origin: 'NewShipmentPage#create',
        body,
      })
      if (response.error) {
        setSaveError(response.message || 'Could not create the shipment')
        return
      }
      setCreated(true)
      setFormKey((key) => key + 1)
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not create the shipment')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <PageContainer>
      <Link to={cameFromTrackers ? '/trackers' : '/admin'} className="link link-hover mb-2 inline-block text-sm">
        ← {cameFromTrackers ? 'Trackers' : 'Admin'}
      </Link>
      <PageHeader
        title="New Shipment"
        subtitle="Enter shipment details to start monitoring"
        icon={<TbTruckDelivery className="h-6 w-6" />}
      />

      {created && <Alert type="success" message="Shipment created" dismissible />}
      {saveError && <Alert type="error" message={saveError} />}

      <ShipmentForm
        key={formKey}
        isSaving={isSaving}
        onSubmit={(body) => void handleSubmit(body)}
        onClear={clearForm}
        onInvalid={() => {
          setCreated(false)
          setSaveError('Some fields still need attention.')
        }}
      />
    </PageContainer>
  )
}

export default NewShipmentPage
